import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { JourneyApplication } from '../../types/readyToFly';
import { FinalDocumentProgressChip } from '../final-documentation/FinalDocumentProgressChip';

/** Final Documentation Checklist (Phase 95): before "Ready to Fly (n)",
 * staff review each selected student's checklist and what's outstanding.
 * Review only — the checklist never blocks or grants Ready to Fly; the
 * existing eligibility checks still decide, exactly as before. */
export const MarkReadyReviewDialog = ({
  open,
  applications,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  applications: JourneyApplication[];
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const incomplete = applications.filter(
    (a) => a.finalDocumentation && !a.finalDocumentation.complete,
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Review Final Documentation before Ready to Fly</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {incomplete.length > 0 ? (
            <Alert severity="warning">
              {incomplete.length} of {applications.length} selected student(s) have outstanding
              checklist documents not yet approved. You can still continue; the usual Ready to Fly eligibility checks
              apply.
            </Alert>
          ) : (
            <Alert severity="success">
              Every selected student's Final Documentation Checklist is complete.
            </Alert>
          )}
          {applications.map((a, index) => {
            const progress = a.finalDocumentation;
            return (
              <Box key={a.id}>
                {index > 0 && <Divider sx={{ mb: 2 }} />}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2">
                    {a.student.firstName} {a.student.lastName} ({a.student.studentNumber})
                  </Typography>
                  {progress && <FinalDocumentProgressChip progress={progress} />}
                </Stack>
                {progress && !progress.complete && (
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                    {progress.outstanding.map((o) => (
                      <Typography key={o.key} component="li" variant="body2" color="text.secondary">
                        {o.label} —{' '}
                        {o.status === 'MISSING' ? 'not uploaded' : o.status === 'PENDING' ? 'pending review' : 'rejected'}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={pending || applications.length === 0}
          onClick={onConfirm}
        >
          {pending ? 'Marking…' : `Mark ${applications.length} Ready to Fly`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
